/* Mock enquiries — list/get/create/reply/close/acceptQuote.
 *
 * Stored as a single list under `enquiries`; filtered by current session
 * role on read. Customers see their own; admin/sales/manager see all.
 *
 * Accepting a quote re-adds the line items to the cart so the customer
 * can immediately checkout the agreed price.
 */

import * as store from "@/data/mocks/storage";
import * as cart from "./cart";
import { withLatency, withError, paginate, pushAudit, getSession } from "./_util";

function readAll() {
  return store.get("enquiries", []) || [];
}

function writeAll(arr) {
  store.set("enquiries", arr);
}

function getCurrentSession() {
  return getSession();
}

function visibleToSession(e, session) {
  if (!session) return e.user_id === "anon";
  if (["admin", "manager", "sales"].includes(session.role)) return true;
  return e.user_id === session.user_id;
}

function isSalesActor(session) {
  return session && ["sales", "manager", "admin"].includes(session.role);
}

function decorate(e) {
  const items_subtotal = (e.items || []).reduce((s, it) => s + (it.line_total || 0), 0);
  return { ...e, subtotal: items_subtotal, tax: Math.round(items_subtotal * 0.05), total: items_subtotal + Math.round(items_subtotal * 0.05) };
}

export async function list(params = {}) {
  const session = getCurrentSession();
  let all = readAll().filter((e) => visibleToSession(e, session));
  if (params.status && params.status !== "all") {
    all = all.filter((e) => e.status === params.status);
  }
  if (params.q) {
    const q = String(params.q).toLowerCase();
    all = all.filter((e) => (e.id || "").toLowerCase().includes(q));
  }
  all = all.slice().sort((a, b) => new Date(b.placed_at) - new Date(a.placed_at));
  return withLatency(paginate(all.map(decorate), params));
}

export async function get(id) {
  const session = getCurrentSession();
  const found = readAll().find((e) => e.id === id);
  if (!found) return withError(`Enquiry ${id} not found`, 404);
  if (!visibleToSession(found, session)) return withError("Not authorised", 403);
  return withLatency(decorate(found));
}

export async function create(payload = {}) {
  const session = getCurrentSession();
  if (!session) return withError("Login required to create an enquiry", 401);
  const items = (payload.items || []).filter((it) => it && it.product_slug && it.qty > 0);
  if (items.length === 0) return withError("At least one item required", 400);
  const all = readAll();
  const year = new Date().getFullYear();
  const sameYear = all.filter((e) => (e.id || "").includes(`-${year}-`));
  const n = sameYear.length + 1;
  const id = `ENQ-${year}-${String(n).padStart(4, "0")}`;
  const now = new Date().toISOString();
  const enq = {
    id,
    user_id: session.user_id,
    user_email: session.email,
    status: "pending",
    placed_at: now,
    items: items.map((it) => ({
      product_slug: it.product_slug,
      product_name: it.product_name || it.product_slug,
      qty: it.qty,
      unit_price: it.unit_price || 0,
      line_total: (it.unit_price || 0) * it.qty,
    })),
    sales_rep: null,
    notes: payload.notes || null,
    thread: [
      { id: "m-" + Math.random().toString(36).slice(2, 8), author: session.email, role: session.role, message: payload.notes || "New enquiry opened.", ts: now },
    ],
  };
  writeAll([enq, ...all]);
  return withLatency(decorate(enq));
}

export async function reply(id, { message } = {}) {
  if (!message) return withError("Message required", 400);
  const session = getCurrentSession();
  if (!session) return withError("Login required", 401);
  const all = readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return withError(`Enquiry ${id} not found`, 404);
  const enq = all[idx];
  if (!visibleToSession(enq, session)) return withError("Not authorised", 403);
  const reply = {
    id: "m-" + Math.random().toString(36).slice(2, 8),
    author: session.email,
    role: session.role,
    message,
    ts: new Date().toISOString(),
  };
  const updated = { ...enq, thread: [...(enq.thread || []), reply] };
  all[idx] = updated;
  writeAll(all);
  return withLatency(decorate(updated));
}

export async function close(id) {
  const session = getCurrentSession();
  const all = readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return withError(`Enquiry ${id} not found`, 404);
  if (!visibleToSession(all[idx], session)) return withError("Not authorised", 403);
  const updated = { ...all[idx], status: "closed", closed_at: new Date().toISOString() };
  all[idx] = updated;
  writeAll(all);
  return withLatency(decorate(updated));
}

export async function acceptQuote(id) {
  const session = getCurrentSession();
  const all = readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return withError(`Enquiry ${id} not found`, 404);
  const enq = all[idx];
  if (!visibleToSession(enq, session)) return withError("Not authorised", 403);
  if (enq.status !== "quoted") {
    return withError("Quote can only be accepted while status is 'quoted'", 400);
  }
  // Side effect — re-add items to the cart at the quoted unit price.
  for (const it of enq.items) {
    try { await cart.add({ slug: it.product_slug, qty: it.qty }); } catch (_) {}
  }
  const updated = { ...enq, status: "confirmed", confirmed_at: new Date().toISOString() };
  all[idx] = updated;
  writeAll(all);
  pushAudit({ actor_id: session?.user_id, actor_role: session?.role, action: "enquiry.accepted", target_type: "enquiry", target_id: id });
  return withLatency(decorate(updated));
}

/* ---- Sales-side mutations (M3) ---- */

export async function quote(id, { items: priced = [], message } = {}) {
  const session = getCurrentSession();
  if (!isSalesActor(session)) return withError("Sales role required", 403);
  if (!priced.length) return withError("At least one line price required", 400);

  const all = readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return withError(`Enquiry ${id} not found`, 404);
  const enq = all[idx];
  if (enq.status === "closed" || enq.status === "rejected") {
    return withError(`Cannot quote on a ${enq.status} enquiry`, 400);
  }

  // Replace line prices, recompute line totals.
  const repricedById = new Map(priced.map((p) => [p.product_slug, p]));
  const newItems = enq.items.map((it) => {
    const patch = repricedById.get(it.product_slug);
    if (!patch) return it;
    const unit = Number(patch.unit_price) || it.unit_price;
    return { ...it, unit_price: unit, line_total: unit * it.qty };
  });

  const thread = (enq.thread || []).slice();
  const now = new Date().toISOString();
  if (message && message.trim()) {
    thread.push({
      id: "m-" + Math.random().toString(36).slice(2, 8),
      author: session.email,
      role: session.role,
      message: message.trim(),
      ts: now,
    });
  }
  thread.push({
    id: "m-" + Math.random().toString(36).slice(2, 8),
    author: session.email,
    role: session.role,
    message: "Quote sent. Prices valid for 7 days.",
    ts: now,
    system: true,
  });

  const updated = {
    ...enq,
    items: newItems,
    status: "quoted",
    sales_rep: enq.sales_rep || (session.email || "sales rep"),
    sales_rep_id: enq.sales_rep_id || session.user_id,
    quoted_at: now,
    thread,
  };
  all[idx] = updated;
  writeAll(all);
  pushAudit({ actor_id: session.user_id, actor_role: session.role, action: "enquiry.quoted", target_type: "enquiry", target_id: id, detail: `${newItems.length} lines` });
  return withLatency(decorate(updated));
}

export async function assignRep(id, { rep_id, rep_name } = {}) {
  const session = getCurrentSession();
  if (!isSalesActor(session)) return withError("Sales role required", 403);
  const all = readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return withError(`Enquiry ${id} not found`, 404);
  const updated = {
    ...all[idx],
    sales_rep: rep_name || session.email,
    sales_rep_id: rep_id || session.user_id,
    assigned_at: new Date().toISOString(),
  };
  all[idx] = updated;
  writeAll(all);
  pushAudit({ actor_id: session.user_id, actor_role: session.role, action: "enquiry.assigned", target_type: "enquiry", target_id: id, detail: updated.sales_rep });
  return withLatency(decorate(updated));
}

export async function rejectEnquiry(id, { reason } = {}) {
  const session = getCurrentSession();
  if (!isSalesActor(session)) return withError("Sales role required", 403);
  if (!reason) return withError("Reason required", 400);
  const all = readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return withError(`Enquiry ${id} not found`, 404);

  const enq = all[idx];
  const thread = (enq.thread || []).concat([{
    id: "m-" + Math.random().toString(36).slice(2, 8),
    author: session.email,
    role: session.role,
    message: `Enquiry rejected: ${reason}`,
    ts: new Date().toISOString(),
    system: true,
  }]);

  const updated = {
    ...enq,
    status: "rejected",
    reject_reason: reason,
    rejected_at: new Date().toISOString(),
    thread,
  };
  all[idx] = updated;
  writeAll(all);
  pushAudit({ actor_id: session.user_id, actor_role: session.role, action: "enquiry.rejected", target_type: "enquiry", target_id: id, detail: reason });
  return withLatency(decorate(updated));
}
